import { useState, useEffect } from "react";
import { getAllAssignmentTypes, createRoutingPriority, getRoutingPriorities } from "../Services/APIService";
import { AssignmentType, RoutingPrioritys, CreateRoutingPriorityPayload } from "../types/routingPriority.types";
import { Toaster, toast } from "react-hot-toast";

export const useRoutingPriority = () => {
  const [assignmentTypes, setAssignmentTypes] = useState<AssignmentType[]>([]);
  const [priorities, setPriorities] = useState<RoutingPrioritys[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [ranks, setRanks] = useState<{ [key: number]: number }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Function to toggle the sidebar
  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  // Function to fetch and initialize data
  const fetchAndInitializeData = async () => {
    setLoading(true);
    try {
      const [typesResponse, prioritiesResponse] = await Promise.all([
        getAllAssignmentTypes(),
        getRoutingPriorities(),
      ]);
      setAssignmentTypes(typesResponse.assignmentTypeList || []);
      setPriorities(prioritiesResponse.routingPriorities || []);
      console.log('Fetched Assignment Types:', typesResponse.assignmentTypeList);

      const initialRanks = (typesResponse.assignmentTypeList || []).reduce((acc: { [key: number]: number }, type: AssignmentType) => {
        acc[type.typeId] = 0;
        return acc;
      }, {});

      const updatedRanks = (prioritiesResponse.routingPriorities || []).reduce((acc: { [key: number]: number }, priority: RoutingPrioritys) => {
        acc[priority.assignmentTypeId] = priority.rank;
        return acc;
      }, {}) || {};

      const finalRanks = { ...initialRanks, ...updatedRanks };
      setRanks(finalRanks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      toast.error(`Failed to load data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndInitializeData();
  }, []);

  const handleRankChange = (typeId: number, value: string) => {
    const numValue = parseInt(value) || 0;
    setRanks((prev) => ({ ...prev, [typeId]: numValue }));
  };

  const handleRowSelected = (state: { selectedRows: any[] }) => {
    setSelectedRows(state.selectedRows);
  };

  const handleClearSelected = () => {
    setToggleCleared(!toggleCleared);
    setSelectedRows([]);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const maxRank = assignmentTypes.length;
      const rankValues = assignmentTypes.map(type => ranks[type.typeId] || 0);
      const rankedValues = rankValues.filter(r => r > 0);
      const uniqueRankedValues = new Set(rankedValues);

      if (rankedValues.some(r => r < 1 || r > maxRank)) {
        toast.error(`Priority must be between 1 and ${maxRank}`);
        setLoading(false);
        return;
      }

      if (rankedValues.length !== uniqueRankedValues.size) {
        toast.error('All Priorities must be unique');
        setLoading(false);
        return;
      }

      const payload: CreateRoutingPriorityPayload = {
        tenantId: Number(localStorage.getItem("tenantId")) || 0,
        routingPriorities: assignmentTypes.map((type) => ({
          assignmentTypeId: type.typeId,
          rank: ranks[type.typeId] || 0,
        })),
        createdBy: Number(localStorage.getItem("UserId")) || 0,
      };

      const response = await createRoutingPriority(payload);
      if (response.isCreated) {
        setIsEditMode(false);
        await fetchAndInitializeData(); // Refresh data after save
        setError(null);
        toast.success('Priorities saved successfully');
      } else {
        setError('Failed to save routing priorities');
        toast.error('Failed to save routing priorities');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save priorities');
      toast.error(`Save failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const tableData = assignmentTypes.map((type) => ({
    id: type.typeId,
    typeId: type.typeId,
    typeName: type.typeName,
    rank: isEditMode ? ranks[type.typeId] || 0 : (priorities.find(p => p.assignmentTypeId === type.typeId)?.rank || 'Not Set'),
  }));

  const filteredData = tableData.filter((item) =>
    item.typeName.toLowerCase().includes(searchText.toLowerCase())
  );

  const testData = filteredData;

  return {
    assignmentTypes,
    priorities,
    isEditMode,
    ranks,
    loading,
    error,
    selectedRows,
    toggleCleared,
    searchText,
    setSearchText,
    setIsEditMode,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    handleRankChange,
    toggleSidebar, // Now defined
    handleRowSelected,
    handleClearSelected,
    handleSave,
    fetchAndInitializeData,
    tableData,
    filteredData,
    testData,
  };
};

export {};